/**
 * Rasteriza el mapa a un canvas: teselas, perímetro, círculos de cobertura,
 * pictogramas de DEA y de edificios.
 *
 * No se usa `html2canvas` ni similares. Las teselas se dibujan una por una
 * ubicándolas por su coordenada geográfica real, lo que hace que el render
 * funcione también con el mapa ROTADO — un screenshot del DOM no lo lograría sin
 * arrastrar la rotación de la propia página.
 */

import L from 'leaflet';
import { destinationPoint } from '../domain/geo.js';
import { EXPORT_SCALE } from '../config.js';
import { hexToRgba } from './canvas-utils.js';

/** Dibuja las teselas cargadas de una capa, respetando la rotación de la vista. */
function drawTileLayer(ctx, map, layer, toPoint) {
  if (!layer?._tiles) return;
  const zoom = layer._tileZoom != null ? layer._tileZoom : Math.round(map.getZoom());
  Object.keys(layer._tiles).forEach((key) => {
    const tile = layer._tiles[key];
    if (!tile?.el || !tile.coords || tile.coords.z !== zoom) return;
    if (!tile.el.complete || !tile.el.naturalWidth) return;
    const bounds = layer._tileCoordsToBounds(tile.coords);
    const nw = toPoint(bounds.getNorthWest());
    const ne = toPoint(bounds.getNorthEast());
    const dx = ne.x - nw.x;
    const dy = ne.y - nw.y;
    const size = Math.hypot(dx, dy) + 1;
    const angle = Math.atan2(dy, dx);
    ctx.save();
    ctx.translate(nw.x, nw.y);
    ctx.rotate(angle);
    try {
      ctx.drawImage(tile.el, 0, 0, size, size);
    } catch {
      // Tesela con CORS denegado: se omite en vez de tumbar la exportación entera.
    }
    ctx.restore();
  });
}

/**
 * @returns {{canvas: HTMLCanvasElement, width: number, height: number, toPoint: Function}}
 */
export function renderMapSource({ map, container, baseLayer, perimeter, deas, buildings, radius, aedImage }) {
  const width = container.offsetWidth;
  const height = container.offsetHeight;
  const scale = EXPORT_SCALE;

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);

  const toPoint = (latLng) => map.latLngToContainerPoint(latLng);

  ctx.fillStyle = '#0e1820';
  ctx.fillRect(0, 0, width, height);

  // Las etiquetas de calle se omiten a propósito: con el mapa rotado el texto de
  // la tesela sale invertido y la lámina queda ilegible.
  drawTileLayer(ctx, map, baseLayer, toPoint);

  if (perimeter.length >= 3) {
    ctx.beginPath();
    perimeter.forEach((point, index) => {
      const p = toPoint(L.latLng(point[0], point[1]));
      if (index) ctx.lineTo(p.x, p.y);
      else ctx.moveTo(p.x, p.y);
    });
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,221,0,.06)';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#ffdd00';
    ctx.stroke();
  }

  deas.forEach((dea) => {
    const center = dea.circle.getLatLng();
    const centerPoint = toPoint(center);
    const edge = destinationPoint(center.lat, center.lng, radius, dea.bearing);
    const edgePoint = toPoint(L.latLng(edge[0], edge[1]));
    const radiusPx = Math.hypot(edgePoint.x - centerPoint.x, edgePoint.y - centerPoint.y);

    ctx.beginPath();
    ctx.arc(centerPoint.x, centerPoint.y, radiusPx, 0, 2 * Math.PI);
    ctx.fillStyle = hexToRgba(dea.color, 0.06);
    ctx.fill();
    // Halo blanco bajo el trazo de color: sobre satélite oscuro un círculo de
    // color solo desaparece.
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(255,255,255,.6)';
    ctx.stroke();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = dea.color;
    ctx.setLineDash(dea.estado === 'propuesto' ? [8, 6] : []);
    ctx.stroke();
    ctx.setLineDash([]);

    const dirX = (edgePoint.x - centerPoint.x) / radiusPx;
    const dirY = (edgePoint.y - centerPoint.y) / radiusPx;
    ctx.strokeStyle = '#ff2a2a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(centerPoint.x, centerPoint.y);
    ctx.lineTo(edgePoint.x, edgePoint.y);
    ctx.stroke();

    // Cota "R 150 m" alineada a la línea y desplazada perpendicularmente, para
    // que el texto no quede cruzado por su propia cota.
    let deg = (Math.atan2(dirY, dirX) * 180) / Math.PI;
    if (deg > 90) deg -= 180;
    if (deg < -90) deg += 180;
    const labelX = centerPoint.x + dirX * radiusPx * 0.55 + -dirY * 11;
    const labelY = centerPoint.y + dirY * radiusPx * 0.55 + dirX * 11;
    ctx.save();
    ctx.translate(labelX, labelY);
    ctx.rotate((deg * Math.PI) / 180);
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 3.5;
    ctx.strokeStyle = 'rgba(16,26,36,.9)';
    ctx.strokeText(`R ${radius} m`, 0, 0);
    ctx.fillStyle = '#fff';
    ctx.fillText(`R ${radius} m`, 0, 0);
    ctx.restore();

    ctx.beginPath();
    ctx.arc(centerPoint.x, centerPoint.y, 5.5, 0, 2 * Math.PI);
    ctx.fillStyle = '#ff2a2a';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
  });

  const hasAed = aedImage?.complete && aedImage.naturalWidth;

  deas.forEach((dea) => {
    const point = toPoint(dea.marker.getLatLng());
    const iconPoint = toPoint(dea.icono.getLatLng());
    ctx.strokeStyle = 'rgba(255,255,255,.85)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineTo(iconPoint.x, iconPoint.y);
    ctx.stroke();
    ctx.setLineDash([]);
    if (hasAed) ctx.drawImage(aedImage, iconPoint.x - 13, iconPoint.y, 26, 34);
    const badgeY = iconPoint.y - 9;
    ctx.beginPath();
    ctx.arc(iconPoint.x, badgeY, 8, 0, 2 * Math.PI);
    ctx.fillStyle = '#14202b';
    ctx.fill();
    ctx.lineWidth = 1.6;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 9px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`D${dea.num}`, iconPoint.x, badgeY);
  });

  // Anclas de los cuadros de edificio. El cuadro NO se dibuja acá: si quedara
  // horneado en el raster, moverlo obligaría a re-renderizar el mapa entero y no
  // se podría arrastrar. Lo dibuja la lámina como capa encima. Ver building-box.js.
  const buildingAnchors = [];

  buildings.forEach((building, index) => {
    const point = toPoint(building.marker.getLatLng());
    const iconPoint = toPoint(building.icono.getLatLng());
    const bw = 20;
    const bh = 34;
    const bx = iconPoint.x - bw / 2;
    const by = iconPoint.y;

    ctx.strokeStyle = 'rgba(255,255,255,.85)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineTo(iconPoint.x, iconPoint.y);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = 'rgba(16,26,36,.55)';
    ctx.fillRect(bx, by, bw, bh);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.strokeRect(bx, by, bw, bh);
    ctx.lineWidth = 1.5;
    [9, 18, 27].forEach((oy) => {
      ctx.beginPath();
      ctx.moveTo(bx, by + oy);
      ctx.lineTo(bx + bw, by + oy);
      ctx.stroke();
    });
    ctx.beginPath();
    ctx.moveTo(bx + bw / 2, by);
    ctx.lineTo(bx + bw / 2, by + bh);
    ctx.stroke();

    const badgeY = by - 9;
    ctx.beginPath();
    ctx.arc(bx + bw / 2, badgeY, 8, 0, 2 * Math.PI);
    ctx.fillStyle = '#2f6db3';
    ctx.fill();
    ctx.lineWidth = 1.6;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 9px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`E${index + 1}`, bx + bw / 2, badgeY);

    ctx.beginPath();
    ctx.arc(point.x, point.y, 5.5, 0, 2 * Math.PI);
    ctx.fillStyle = '#2f6db3';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#fff';
    ctx.stroke();

    buildingAnchors.push({ index, x: bx + bw, y: by + bh / 2 });
  });

  return { canvas, width, height, toPoint, buildingAnchors };
}
